import { Component, Input, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Workspace } from 'app/global/user-settings.dto';
import { WorkspaceService } from '../navbar/workspace.service';
import { GlobalService } from 'app/global/global.service';
import { ProfileService } from 'app/main/pages/profile-settings/profile.service';
import { TranslocoService } from '@jsverse/transloco';
import { UserSettingsFacade } from 'app/store/user-settings.facade';

@Component({
  selector: 'app-workspace-form-modal',
  templateUrl: './workspace-form-modal.component.html',
  styleUrl: './workspace-form-modal.component.scss'
})
export class WorkspaceFormModalComponent implements OnInit {
 
  @Input() workspace: Workspace;
  
  public workSpaceForm$: FormGroup;
  public loading: boolean = false;
  public userSettings$ = this._userSettingsFacade._userSettings$;
  public submitted: boolean = false;

  constructor (
    private _formBuilder$: FormBuilder,
    private _activeModal$: NgbActiveModal,
    private _workSpaceService$: WorkspaceService,
    private _globalService$: GlobalService,
    private _profileService$: ProfileService,
    private _translocoService$: TranslocoService,
    private _userSettingsFacade: UserSettingsFacade,
  ) {}


  ngOnInit(): void {
    this.initWorkSpaceForm(this.workspace);
  }




  /**
   * Initializes the reactive form for a workspace using the provided `Workspace` object.
   * Sets up form controls with default values and validators for each workspace property.
   *
   * @param workspace - The workspace object containing initial values for the form fields.
   * 
   * Form controls:
   * - `id`: Workspace identifier (nullable).
   * - `name`: Workspace name (required).
   * - `organization`: Organization name (required).
   * - `registration_number`: Registration number (required).
   * - `address`: Address (required).
   * - `zipcode`: Zip code (required).
   * - `city`: City (required).
   * - `phone`: Phone number (optional).
   * - `default`: Indicates if this is the default workspace (boolean).
   * - `is_organization`: Indicates if this is an organization (boolean, to be removed in the future).
   */
  initWorkSpaceForm(workspace: Workspace): void {
    this.workSpaceForm$ = this._formBuilder$.group({
      id: [workspace?.id ?? null],
      name: [workspace?.name ?? null, [Validators.required]],
      organization: [workspace?.organization ?? null, [Validators.required]],
      registration_number: [workspace?.registration_number ?? null, [Validators.required]],
      address: [workspace?.address ?? null, [Validators.required]],
      zipcode: [workspace?.zipcode ?? null, [Validators.required]],
      city: [workspace?.city ?? null, [Validators.required]],
      phone: [workspace?.phone ?? null],
      default: [workspace?.default ?? false],
      is_organization: [workspace?.is_organization ?? false],
    });
  }




  get form(){
    return this.workSpaceForm$.controls;
  }



  /**
   * Closes the modal dialog and passes the provided workspace data to the caller.
   *
   * @param workSpace - The workspace object to return when closing the modal.
   */
  closeModal(workSpace: Workspace): void {
    this._activeModal$.close(workSpace);
  }






  /**
   * Handles the submission of the workspace form.
   *
   * - Marks the form as submitted.
   * - Validates the form; if invalid, the submission is aborted.
   * - Sets the loading state to true.
   * - Normalizes the phone number field to its E.164 format if available.
   * - Determines whether to update an existing workspace or create a new one
   *   based on the presence of an 'id' field in the form.
   */
  submitWorkSpaceForm(): void {
    this.submitted = true;
    if(this.workSpaceForm$.invalid) return;

    this.loading = true;
    const phoneControl = this.workSpaceForm$.get('phone');
    if (phoneControl?.value?.e164Number) {
      phoneControl.setValue(phoneControl.value.e164Number);
    }
    if(this.workSpaceForm$?.get('id')?.value){
      this.updateWorkSpace()
    } else {
      this.createWorkSpace();
    }
  }




  
  /**
   * Creates a new workspace using the form data and handles the response.
   *
   * Submits the current form values to the workspace service to create a new workspace.
   * On success, closes the active modal and passes the response with an added `default: false` property.
   * On error, displays an error message using the global service and resets the loading and submitted states.
   */
  createWorkSpace(): void {
    this._workSpaceService$.createWorkspace(this.workSpaceForm$.value).subscribe({
      next: (response) => {
        this._activeModal$.close({...response, default: false});
      },
      error: (error) => {
        this._globalService$.toastrError(error);
        this.loading = false;
        this.submitted = false;
      }
    });
  }



  /**
   * Updates the workspace using the form values and handles the response.
   *
   * Submits the current workspace form data to the workspace service for updating.
   * On success, closes the active modal and triggers a workspace change event in the profile service.
   * On error, displays a toast error message and resets the loading and submitted states.
   */
  updateWorkSpace(): void {
    this._workSpaceService$.updateWorkspace(this.workSpaceForm$.value).subscribe({
      next: (response) => {
        this._activeModal$.close({...response, default: this.workSpaceForm$.value.default});
        this._profileService$.setWorkSpaceChangesTrigger(true);
      },
      error: (error) => {
        this._globalService$.toastrError(error);
        this.loading = false;
        this.submitted = false;
      }
    })
  }


   
  /**
   * Displays a confirmation dialog to the user before deleting a workspace.
   * 
   * The dialog prompts the user to type "delete" to confirm the deletion.
   * If the user confirms and enters the correct text, the `deleteWorkspace` method is called.
   * Uses SweetAlert2 for the modal dialog.
   *
   * @remarks
   * - Shows a warning icon and requires explicit user input for confirmation.
   * - If the input does not match "delete", a validation message is shown and deletion is not performed.
   */
  confirmDeleteWorkspace(): void {
    Swal.fire({
      title: this._translocoService$.translate('User confirmation (general) - Are you sure'),
      html: `${this._translocoService$.translate('To delete this workspace please type toaster confirmation text (Workspace form modal) - To delete this workspace please type')} <b>${this._translocoService$.translate('Delete (general) - Delete')?.toLowerCase()}</b> below.<br><input id="swal-input" class="swal2-input" placeholder="delete">`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this._translocoService$.translate('Delete (general) - Delete'),
      cancelButtonText: this._translocoService$.translate('Cancel button title (cancel button) - Cancel'),
      preConfirm: () => {
        const input = (Swal.getPopup()?.querySelector('#swal-input') as HTMLInputElement)?.value;
        if (input?.toLowerCase() !== 'delete') {
          Swal.showValidationMessage(this._translocoService$.translate('You must type delete to confirm text (Workspace form modal) - You must type delete to confirm'));
          return false;
        }
        return true;
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteWorkspace();
      }
    });
  }




  
  /**
   * Deletes the current workspace using the workspace service.
   * 
   * - Checks if the workspace ID exists in the form; if not, exits early.
   * - Sets the loading state to true while the deletion is in progress.
   * - Calls the workspace service to delete the workspace by ID.
   * - On success, closes the modal and returns the deleted workspace's ID.
   * - On error, displays an error message using the global service and resets the loading state.
   */
  deleteWorkspace(): void {
    if (!this.form.id.value) return;
    this.loading = true;
    this._workSpaceService$.deleteWorkspace(this.form.id.value).subscribe({
      next: () => {
        this._activeModal$.close({ deleted: true, id: this.form.id.value });
      },
      error: (error) => {
        this._globalService$.toastrError(error);
        this.loading = false;
      }
    });
  }
}
